// The shadcn/Radix Toaster this used to mount (@/components/ui/toaster,
// backed by useToast()) is dead scaffolding -- nothing in the app ever
// calls useToast() to push to it. Every toast.error()/toast.success()
// call across the app goes through sonner directly instead, which had
// no renderer mounted anywhere, making those calls silent no-ops. This
// is the real one.
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { handleNativeAuthRedirect } from '@/lib/googleAuth';
import { Toaster } from "@/components/ui/sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { TenantBrandingProvider } from '@/lib/TenantBrandingContext';
import { AIDispatchProvider } from '@/lib/AIDispatchContext';
import { ThemeProvider } from '@/lib/ThemeContext';
import { initNativeShell, hideSplashScreen, notifyAppReady } from '@/lib/nativeShell';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ErrorBoundary from '@/components/ErrorBoundary';
import LandingPage from './pages/LandingPage';
import Layout from './components/Layout';
import LoadBoard from './pages/LoadBoard';
import ActiveLoad from './pages/ActiveLoad';
import Financials from './pages/Financials';
import AdminPanel from './pages/AdminPanel';
import DriverProfile from './pages/DriverProfile';
import TeamChat from './pages/TeamChat';
import Onboarding from './pages/Onboarding.jsx';
import OnboardingSuccess from './pages/OnboardingSuccess';
import CarrierAuth from './pages/CarrierAuth';
import AcceptInvite from './pages/AcceptInvite';
import ResetPassword from './pages/ResetPassword';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';

// Routes that must render with NO auth gate (Stripe verification,
// search engines, prospective customers, and Stripe Checkout's footer
// links all need to reach these without logging in).
const PUBLIC_LEGAL_PATHS = ['/terms', '/privacy'];

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, isAuthenticated, hasCarrier, isPasswordRecovery } = useAuth();
  const location = useLocation();

  // Native app shell setup -- no-ops on the plain web/PWA build. Status
  // bar/keyboard config happens immediately; the splash screen is only
  // dismissed once auth/data loading actually resolves (see
  // capacitor.config.ts's SplashScreen.launchAutoHide: false), so there's
  // no flash of the loading spinner between the splash disappearing and
  // the app having something real to show.
  //
  // Safety net: if auth/settings loading hangs for any reason (e.g. an
  // auth-library internal lock that never resolves in some WebView
  // configurations), loadingTimedOut forces both the native splash away
  // AND the web spinner gate below to fall through to a real screen
  // (CarrierAuth, since isAuthenticated/hasCarrier are still false at
  // that point) instead of spinning forever with no way out. A normal
  // fast load never touches this -- it only fires if still stuck at 8s.
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  useEffect(() => { initNativeShell(); }, []);
  useEffect(() => {
    if (!isLoadingAuth && !isLoadingPublicSettings) {
      hideSplashScreen();
      // Confirms this bundle rendered successfully -- see notifyAppReady's
      // own comment for why this must happen here and not on script load.
      notifyAppReady();
    }
  }, [isLoadingAuth, isLoadingPublicSettings]);
  useEffect(() => {
    const timer = setTimeout(() => { setLoadingTimedOut(true); hideSplashScreen(); notifyAppReady(); }, 8000);
    return () => clearTimeout(timer);
  }, []);

  // Legal pages are public and render BEFORE any auth/loading gate.
  // Stripe activation verification, search engines, and unauthenticated
  // prospects all need to reach /terms and /privacy without a session
  // and without an interstitial spinner.
  if (PUBLIC_LEGAL_PATHS.includes(location.pathname)) {
    return (
      <Routes>
        <Route path="/terms"   element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
      </Routes>
    );
  }

  if ((isLoadingPublicSettings || isLoadingAuth) && !loadingTimedOut) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  // Belt-and-suspenders for the /reset-password bypass below: that one
  // only works if Supabase's redirect_to actually landed the browser on
  // /reset-password. If the project's Redirect URLs allowlist doesn't
  // include it, Supabase silently falls back to the bare Site URL
  // instead -- the recovery session still gets established (confirmed
  // by AuthContext's PASSWORD_RECOVERY event, which fires regardless of
  // path), but it lands here on whatever the fallback path is instead,
  // dropping the user straight into the normal app (Onboarding, etc.)
  // before they've ever set a new password. Honor the event over the
  // path.
  if (isPasswordRecovery && location.pathname !== '/reset-password') {
    return <Navigate to="/reset-password" replace />;
  }

  // /accept-invite is a public-ish route -- it must work for users who
  // are not yet authenticated AND for authenticated users who don't yet
  // have a carrier (the invite IS what gives them one). Bypass the
  // CarrierAuth gate before checking it.
  if (location.pathname === '/accept-invite') {
    return (
      <Routes>
        <Route path="/accept-invite" element={<AcceptInvite />} />
      </Routes>
    );
  }
  // Same reasoning as /accept-invite: clicking a password-reset email
  // link makes supabase-js establish a session automatically before
  // this ever renders. Bypassing the CarrierAuth gate keeps that
  // session from getting auto-routed straight into the app before the
  // user actually sets a new password.
  if (location.pathname === '/reset-password') {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    );
  }
  if (!isAuthenticated || !hasCarrier) {
    return <CarrierAuth />;
  }
  // authenticated + has carrier -> falls through to the routed app below

  return (
    <Routes>
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/onboarding-success" element={<OnboardingSuccess />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/register" element={<Navigate to="/" replace />} />
      <Route element={<Layout />}>
        <Route path="/" element={<LoadBoard />} />
        {/* super_admin's Load Board nav item -- see Layout.jsx's / redirect,
            which now sends every staff member to /admin instead. */}
        <Route path="/load-board" element={<LoadBoard />} />
        <Route path="/active-load" element={<ActiveLoad />} />
        <Route path="/financials" element={<Financials />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/my-profile" element={<DriverProfile />} />
        <Route path="/team-chat" element={<TeamChat />} />
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Routes>
  );
};

function App() {
  // Google sign-in's session hand-off, on native only -- Android hands the
  // OAuth redirect back to this app as an appUrlOpen event (the custom-
  // scheme deep link registered in AndroidManifest.xml) instead of a page
  // load, so there's no other point in the app where this URL would
  // otherwise be seen. Registered once here, not per-page, since the
  // redirect can arrive while CarrierAuth isn't even mounted (e.g. sign-in
  // was kicked off, the app got backgrounded, then resumed).
  //
  // Also checked directly via getLaunchUrl() on mount, not just the live
  // listener: if Android killed this app's process while it was
  // backgrounded for the OAuth browser tab (common on a memory-constrained
  // emulator) and the redirect intent cold-starts it fresh, that fresh
  // React tree's listener registration can lose the race against
  // Capacitor's native side already having fired the event -- getLaunchUrl
  // returns the same URL that launched this instance regardless of timing.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listener = CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      handleNativeAuthRedirect(url);
    });
    CapacitorApp.getLaunchUrl().then(result => {
      if (result?.url) handleNativeAuthRedirect(result.url);
    });
    return () => { listener.then(l => l.remove()); };
  }, []);

  return (
    <ThemeProvider>
      {/* ThemeProvider is OUTERMOST so the .dark class on <html> is
          set during the initial paint -- prevents the brief flash of
          light theme on first load for dark-mode users. */}
      {/* TenantBrandingProvider is independent of AuthProvider -- its
          hostname->carrier lookup fires on the same initial commit as
          AuthProvider's session check, in parallel, not sequenced behind
          it. Powers CarrierAuth's subdomain-scoped branded sign-in. */}
      <TenantBrandingProvider>
        <AuthProvider>
          {/* AIDispatchProvider sits at the app root so chat state persists
              across client-side navigation but resets on a full browser
              refresh -- matches what users expect from an embedded chat. */}
          <AIDispatchProvider>
            <QueryClientProvider client={queryClientInstance}>
              {/* Below ThemeProvider (so the fallback UI's bg-background/
                  text-foreground still respect dark/light mode) but above
                  everything else -- a render crash anywhere in routing,
                  Layout, or a page now shows an actual error instead of
                  silently blanking the whole app. */}
              <ErrorBoundary>
                <Router>
                  <AuthenticatedApp />
                </Router>
              </ErrorBoundary>
              <Toaster />
            </QueryClientProvider>
          </AIDispatchProvider>
        </AuthProvider>
      </TenantBrandingProvider>
    </ThemeProvider>
  )
}

export default App
